<template>
    <div class="themed-dropdown" ref="dropdown">
        <div @click="toggleDropdown" class="themed-dropdown__trigger">
            <slot name="trigger">
                <themed-button 
                    type="button" 
                    variant="secondary"
                    class="themed-dropdown__button"
                >
                    <span>{{ label }}</span>
                    <theme-icon icon="chevron-down-icon" type="icons" />
                </themed-button>
            </slot>
        </div>

        <transition name="dropdown-fade">
            <div 
                v-if="isOpen" 
                class="themed-dropdown__menu"
                :class="menuClass"
                role="menu"
            >
                <!-- Configuration-based actions (if actions prop provided) -->
                <ul v-if="actions && actions.length" class="themed-dropdown__actions-list">
                    <li 
                        v-for="(action, index) in visibleActions" 
                        :key="index"
                        role="none"
                        class="themed-dropdown__action-item"
                    >
                        <!-- Link Action -->
                        <a
                            v-if="action.type === 'link'"
                            :href="action.href"
                            :target="action.target || '_self'"
                            :rel="action.target === '_blank' ? 'noopener noreferrer' : null"
                            class="themed-dropdown__action-link"
                            :class="[action.class, { 'themed-dropdown__action-link--danger': action.danger }]"
                            role="menuitem"
                            :aria-label="action.ariaLabel || action.label"
                            @click="handleActionClick(action, $event)"
                        >
                            <theme-icon 
                                v-if="action.icon" 
                                :icon="action.icon" 
                                type="icons" 
                                class="themed-dropdown__action-icon"
                            />
                            <span>{{ action.label }}</span>
                        </a>

                        <!-- Button Action -->
                        <button
                            v-else
                            @click="handleActionClick(action, $event)"
                            type="button"
                            class="themed-dropdown__action-button"
                            :class="[action.class, { 'themed-dropdown__action-button--danger': action.danger }]"
                            role="menuitem"
                            :aria-label="action.ariaLabel || action.label"
                            :disabled="action.disabled"
                        >
                            <theme-icon 
                                v-if="action.icon" 
                                :icon="action.icon" 
                                type="icons" 
                                class="themed-dropdown__action-icon"
                            />
                            <span>{{ action.label }}</span>
                        </button>

                        <!-- Divider -->
                        <div 
                            v-if="action.divider" 
                            class="themed-dropdown__divider"
                            role="separator"
                        ></div>
                    </li>
                </ul>

                <!-- Slot-based content (backward compatible) -->
                <slot v-else></slot>
            </div>
        </transition>
    </div>
</template>

<script>
import ThemedButton from './ThemedButton.vue';

export default {
    name: 'ThemedDropdown',
    
    components: {
        ThemedButton
    },
    
    props: {
        label: {
            type: String,
            default: 'Options'
        },
        menuClass: {
            type: String,
            default: ''
        },
        // New: Configuration-based actions
        actions: {
            type: Array,
            default: null,
            // Expected format:
            // [
            //   {
            //     label: 'Edit',
            //     icon: 'edit',
            //     action: 'edit',
            //     show: true,
            //     disabled: false,
            //     danger: false,
            //     class: '',
            //     ariaLabel: 'Edit item',
            //     type: 'button', // or 'link'
            //     href: '/path', // for type: 'link'
            //     target: '_blank', // for type: 'link'
            //     divider: false, // show divider after this item
            //     handler: Function // optional direct handler
            //   }
            // ]
        }
    },

    data() {
        return {
            isOpen: false
        }
    },

    computed: {
        visibleActions() {
            if (!this.actions) return [];
            return this.actions.filter(action => {
                // Show action if 'show' is not defined or is true
                return action.show !== false;
            });
        }
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },

    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen;
        },

        closeDropdown() {
            this.isOpen = false;
        },

        handleClickOutside(event) {
            if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
                this.closeDropdown();
            }
        },

        handleActionClick(action, event) {
            if (action.disabled) {
                event.preventDefault();
                return;
            }

            // Close dropdown after action (unless it's a link opening in new tab)
            if (action.type !== 'link' || action.target !== '_blank') {
                this.closeDropdown();
            }

            // Emit the action name/identifier
            this.$emit('action', action.action || action.label);
            
            // If action has a handler function, call it
            if (typeof action.handler === 'function') {
                action.handler();
            }
        }
    }
}
</script>
